import React, { useMemo } from 'react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useMergeProps from '../../../../hooks/useMergeProps';
import useRouter from '../../../../hooks/useRouter';
import { selectUserConnected } from '../../../../selectors/user/userPrimary.selector';
import { requestRefreshUserMissionsState } from '../../actions/userMissions';
import { MissionBannerContext } from '../../contexts/MissionBannerContext';
import useMissionTransitionSelector from '../../hooks/useMissionTransitionSelector';

import MissionBanner from './MissionBanner';
import selectMissionBannerContainerProps from './MissionBanner.selector';

type MissionBannerContainerProps = {
  className?: string;
  missionId: number;
};

const MissionBannerContainer = (ownProps: MissionBannerContainerProps) => {
  const dispatch = useDispatch();
  const { missionId } = ownProps;
  const { history, location } = useRouter();

  const isUserConnected = useSelector(selectUserConnected);
  const selectorProps = useMemo(() => ({ missionId, history, dispatch }), [missionId, history, dispatch]);

  const selectedProps = useMissionTransitionSelector(selectMissionBannerContainerProps, selectorProps, {
    disabled: !isUserConnected,
  });

  const { betRedirectionOnClick, operationType } = selectedProps;

  const onClick = useCallback(() => {
    const hash = `#mission-details-${missionId}`;

    if (location.hash !== hash) {
      dispatch(requestRefreshUserMissionsState());

      history.push({
        hash,
      });
    }
  }, [history, missionId, location, dispatch]);

  const otherProps = useMemo(() => ({ onClick }), [onClick]);

  const enhancedProps = useMergeProps({ ownProps, selectedProps, otherProps });

  const forwardedState = useMemo(
    () => ({
      betRedirectionOnClick,
      operationType,
    }),
    [betRedirectionOnClick, operationType],
  );

  return (
    <MissionBannerContext.Provider value={forwardedState}>
      <MissionBanner {...enhancedProps} />
    </MissionBannerContext.Provider>
  );
};

export default React.memo<MissionBannerContainerProps>(MissionBannerContainer);
